<template>
  <div class="digital-business-card-main">
    <b-navbar class="dbc-navbar d-flex">
      <!-- <b-navbar-brand href="#">
        <amg-icon icon="AmgIcon" size="40" class="pointer invert" />
      </b-navbar-brand> -->

      <!-- <b-navbar-toggle target="nav-collapse"></b-navbar-toggle> -->
      <b-collapse id="nav-collapse" is-nav>
        <b-navbar-nav class="ml-auto d-flex align-items-center">
          <!-- <b-nav-item href="#" class="mr-2">
            <b-form-select
              size="sm"
              v-model="selectedLanguage"
              :options="languageOptions"
              class="language-select"
            ></b-form-select>
          </b-nav-item> -->
          <b-nav-item href="#" class="mr-2">
            <dark-toggler class="invert" :size="'20'" />
          </b-nav-item>
        </b-navbar-nav>
      </b-collapse>
    </b-navbar>
    <b-container class="main-container">
      <Skeleton v-if="isLoading" />

      <CardTemplate
        v-if="!isLoading && isValidLink"
        :template="template"
        :data="cardData"
        :selected-language="selectedLanguage"
        :is-saving-contact="isSavingContact"
        @saveContact="downloadVCard()"
      />

      <InvalidLink
        v-if="!isLoading && !isValidLink"
        :selected-language="selectedLanguage"
      />
    </b-container>
  </div>
</template>
<script>
import DarkToggler from "@core/layouts/components/app-navbar/components/DarkToggler.vue";
import DigitalBusinessCardService from "@/views/pages/digital-business-card/service/digital-business-card.service.js";
import CardTemplate from "@/views/pages/digital-business-card/components/CardTemplate.vue";
import InvalidLink from "@/views/pages/digital-business-card/components/InvalidLink.vue";
import Skeleton from "@/views/pages/digital-business-card/components/Skeleton.vue";
import translations from "@/views/pages/digital-business-card/data/translations.js";

export default {
  name: "DigitalBusinessCardMain",
  components: {
    DarkToggler,
    InvalidLink,
    CardTemplate,
    Skeleton,
  },
  data() {
    return {
      languageOptions: [
        { value: "es", text: "Español" },
        { value: "en", text: "English" },
      ],
      selectedLanguage: "en",

      isLoading: false,
      isValidLink: false,
      template: null,
      cardData: null,
      isSavingContact: false,
    };
  },
  async created() {
    await this.checkIsValidLink();
  },
  computed: {
    role() {
      return this.$route.meta.role;
    },
    uniqueCode() {
      return this.$route.params.unique_code;
    },
    getParams() {
      return {
        role: this.role,
      };
    },
    t() {
      return translations[this.selectedLanguage] || translations.en;
    },
  },
  methods: {
    async checkIsValidLink() {
      try {
        this.isLoading = true;

        const valid = await DigitalBusinessCardService.isValidLink(
          this.uniqueCode,
          this.getParams
        );

        this.isValidLink = valid;

        if (!this.isValidLink) this.isLoading = false;
      } catch (error) {
        this._handleError(error);
      }
    },
    async getLinkData() {
      try {
        const data = await DigitalBusinessCardService.getLinkData(
          this.uniqueCode,
          this.getParams
        );

        this.template = data.template;
        this.cardData = data;
        this.updateMetaTags(data);
      } catch (error) {
        this._handleError(error);
      } finally {
        this.isLoading = false;
      }
    },
    async downloadVCard() {
      try {
        this.isSavingContact = true;

        await DigitalBusinessCardService.validateVCard(
          this.uniqueCode,
          this.getParams
        );

        const PATH = `${process.env.VUE_APP_BASE_URL}/commons/digital-business-card/public/download-vcard`;
        const query = new URLSearchParams(this.getParams).toString();
        window.location.href = `${PATH}/${this.uniqueCode}?${query}`;
      } catch (error) {
        this.showErrorSwal(this.t.vCardErrorMessage, this.t.vCardErrorTitle);
      } finally {
        this.isSavingContact = false;
      }
    },
    _handleError(error) {
      const { response } = error;
      const { data } = response || {};
      this.showErrorSwal(data?.message ?? error, this.t.handleErrorMessage);
    },
    updateMetaTags(data) {
      const {
        first_name,
        last_name,
        avatar,
        job_title,
        website,
        type,
        department_name,
      } = data;

      const fullName =
        type === "DEPARTMENT" ? department_name : `${first_name} ${last_name}`;

      const title = job_title
        ? `${fullName} - ${job_title}`
        : `${fullName} | Digital Business Card`;
      const description = this._generateDescription(data, title);
      const image = avatar.url || this._generateInitialsAvatar(data);
      const url = window.location.href;

      document.title = title;

      const metaTags = this._buildMetaTags({
        title,
        description,
        image,
        url,
        website,
      });

      this._applyMetaTags(metaTags);
    },
    _generateDescription(data, title) {
      const { phone, direct_line, email, website, social_media = [] } = data;

      const formatPlatform = (platform) => {
        return (
          platform.charAt(0).toUpperCase() + platform.slice(1).toLowerCase()
        );
      };

      const socialText =
        social_media.length > 0
          ? social_media
              .map((s) => {
                const platform = formatPlatform(s.platform);
                return `${platform}(${s.user_name})`;
              })
              .join(", ")
          : null;

      const parts = [
        `${title}.`,
        `Contact me at ${phone}.`,
        direct_line ? `Direct Line: ${direct_line}.` : "",
        email ? `Email: ${email}.` : "",
        website ? `Website: ${website}.` : "",
        socialText ? `Follow me on ${socialText}.` : "",
      ];

      return parts.filter(Boolean).join(" ");
    },
    _generateInitialsAvatar(data) {
      const { first_name, last_name, type, department_name } = data;

      let initials = "";

      if (type === "DEPARTMENT") {
        initials = department_name
          .split(" ")
          .filter((n) => n.trim().length > 0)
          .slice(0, 2)
          .map((n) => n[0])
          .join("")
          .toUpperCase();
      } else {
        const first = first_name?.trim().charAt(0).toUpperCase() || "";
        const last = last_name?.trim().charAt(0).toUpperCase() || "";
        initials = (first + last).trim();
      }

      return `https://ui-avatars.com/api/?name=${encodeURIComponent(
        initials
      )}&background=random&color=fff`;
    },
    _buildMetaTags({ title, description, image, url, website }) {
      const tags = [
        // --- Standard SEO ---
        { name: "description", content: description },
        { name: "author", content: title },
        { name: "robots", content: "index, follow" },

        // --- Open Graph / Facebook / LinkedIn ---
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:image", content: image },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        { property: "og:locale", content: "en_US" },
        ...(website ? [{ property: "og:site_name", content: website }] : []),

        // --- Twitter ---
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        { name: "twitter:image", content: image },
        { name: "twitter:url", content: url },
        ...(website ? [{ name: "twitter:site", content: website }] : []),

        // --- Structured data (Google) ---
        { itemprop: "name", content: title },
        { itemprop: "description", content: description },
        { itemprop: "image", content: image },
      ];

      return tags;
    },
    _applyMetaTags(tags) {
      tags.forEach(({ property, name, itemprop, content }) => {
        const selector = property
          ? `meta[property="${property}"]`
          : name
          ? `meta[name="${name}"]`
          : `meta[itemprop="${itemprop}"]`;

        let el = document.querySelector(selector);
        if (!el) {
          el = document.createElement("meta");
          if (property) el.setAttribute("property", property);
          if (name) el.setAttribute("name", name);
          if (itemprop) el.setAttribute("itemprop", itemprop);
          document.head.appendChild(el);
        }
        el.setAttribute("content", content);
      });
    },
  },
  watch: {
    isValidLink(newValue) {
      if (newValue !== null && newValue === true) {
        this.getLinkData();
      }
    },
  },
};
</script>
<style lang="scss">
#app {
  --white: #fff;
  --color-gray-900: oklch(21% 0.034 264.665);

  --bg-app: var(--white);
  --bg-navbar: var(--white);
  --bg-main-container: var(--white);
  --box-shadow-navbar: 0 4px 24px 0 rgba(34, 41, 47, 0.1);

  background-color: var(--bg-app);
  .digital-business-card-main {
    background-color: var(--bg-app);
    .dbc-navbar {
      background-color: var(--bg-navbar);
      box-shadow: var(--box-shadow-navbar);
      .b-navbar-nav,
      .b-nav-item,
      .b-navbar-brand,
      .b-navbar-toggle {
        display: flex;
        align-items: center;
      }
      .language-select {
        width: auto;
        height: 35px;
        font-size: 1rem;
        background-color: var(--bg-navbar);
      }
    }
    .main-container {
      padding-bottom: 1rem;
      background-color: var(--bg-main-container);
    }
  }
}

.dark-layout {
  #app {
    --bg-app: var(--color-gray-900);
    --bg-navbar: var(--color-gray-900);
    --bg-main-container: var(--color-gray-900);
    --box-shadow-navbar: 0 4px 24px 0 rgba(0, 0, 0, 0.5);

    .digital-business-card-main {
      .invert {
        filter: invert(100%);
      }
    }
  }
}
</style>
